import { useCallback, useEffect, useState } from "react";
import { Boxes, Clapperboard, Download, Mic2, RefreshCw, ShieldCheck, TriangleAlert } from "lucide-react";
import { ModuleGuide, useModuleCopy } from "./ModuleGuide";

import {
  buildProductionGraph,
  exportProduction,
  loadProductionAssets,
  registerProductionAsset,
  requestProductionTts,
  type ProductionAsset,
  type ProductionGraph,
  type StudioAuth,
} from "../api/client";

type Props = { apiOrigin: string; projectId: string; unitId: string; unitTitle: string; auth: StudioAuth };

export function ProductionPanel({ apiOrigin, projectId, unitId, unitTitle, auth }: Props) {
  const [graph, setGraph] = useState<ProductionGraph | null>(null);
  const copy = useModuleCopy();
  const [assets, setAssets] = useState<ProductionAsset[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshAssets = useCallback(async () => {
    setAssets(await loadProductionAssets({ apiOrigin, projectId, auth }));
  }, [apiOrigin, auth, projectId]);

  useEffect(() => {
    setGraph(null);
    void refreshAssets().catch((cause) => setError(cause instanceof Error ? cause.message : "制作资产加载失败。"));
  }, [refreshAssets, unitId]);

  async function act(key: string, action: () => Promise<string>) {
    setBusy(key); setError(null); setNotice(null);
    try { setNotice(await action()); await refreshAssets(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "制作操作失败。"); }
    finally { setBusy(null); }
  }

  return (
    <main className="production-panel" aria-label="制作工作区">
      <header className="production-header">
        <div><span className="eyebrow">CANONICAL → PRODUCTION GRAPH</span><h1>制作 · {unitTitle}</h1><p>Production Graph 与 Narrative Graph 分离；所有正式资产只绑定 Canonical Commit。</p></div>
        <button type="button" disabled={busy !== null} onClick={() => { void act("graph", async () => { const next = await buildProductionGraph({ apiOrigin, projectId, unitId, auth }); setGraph(next); return "Production Graph 已从当前 Canonical 文本构建。"; }); }}><RefreshCw />{graph ? "刷新图谱" : "生成 Breakdown"}</button>
      </header>
      {error ? <p className="inline-error" role="alert">{error}</p> : null}
      {busy ? <p role="status">{copy.working}</p> : null}
      {notice ? <p className="production-notice" role="status"><ShieldCheck />{notice}</p> : null}
      <ModuleGuide module="production" />
      <section className="production-grid">
        <article className="production-card">
          <div className="context-card-title"><Boxes /><div><h2>Production Graph</h2><p>{graph ? `${graph.nodes.length} nodes · commit ${graph.source_commit_id.slice(0, 12)}` : "尚未针对当前场景建立制作图谱"}</p></div></div>
          {graph ? <div className="production-node-list">{graph.nodes.map((node) => <div key={node.id}><span>{node.node_type}</span><strong>{node.label}</strong><em className={node.status === "fresh" ? "fresh" : "stale"}>{node.status}</em></div>)}</div> : <p className="empty-world">点击“生成 Breakdown”，确定镜头、对白、配音与 Storyboard 的精确依赖。</p>}
        </article>
        <article className="production-card">
          <div className="context-card-title"><Clapperboard /><div><h2>Adapters</h2><p>视频生成器只作为 adapter；输出先进入人工验收队列。</p></div></div>
          <div className="production-actions">
            <button type="button" disabled={!graph || busy !== null} onClick={() => { void act("storyboard", async () => { const node = graph?.nodes.find((item) => item.node_type === "storyboard") ?? graph?.nodes[0]; if (!graph || !node) throw new Error("请先构建 Production Graph。"); await registerProductionAsset({ apiOrigin, projectId, graphId: graph.id, nodeId: node.id, assetType: "storyboard", adapterKey: "manual-storyboard.v1", dependencyAspects: ["full_scene", "appearance", "location"], auth }); return "Storyboard job 已登记，尚未生成或接受任何媒体。"; }); }}>登记 Storyboard</button>
            <button type="button" disabled={busy !== null} onClick={() => { void act("tts", async () => { const result = await requestProductionTts({ apiOrigin, projectId, unitId, voiceId: "zh-CN-XiaoxiaoNeural", locale: "zh-CN", speed: 1, style: "narrative", auth }); return result.cacheHit ? "已命中 Canonical TTS 缓存。" : `TTS job ${result.status}，等待 xhalo-tts 处理。`; }); }}><Mic2 />Canonical TTS</button>
            <button type="button" disabled={busy !== null} onClick={() => { void act("export", async () => { const result = await exportProduction({ apiOrigin, projectId, format: "production_manifest", auth }); return `Production Manifest ${result.id} 已生成。`; }); }}><Download />导出 Manifest</button>
          </div>
        </article>
        <article className="production-card production-assets">
          <div className="context-card-title"><ShieldCheck /><div><h2>Asset Registry</h2><p>只显示当前 Tenant；Canonical 改动按依赖维度标记 possibly_stale。</p></div></div>
          {assets.length ? <div className="asset-list">{assets.map((asset) => <div key={asset.id}><strong>{asset.asset_type}</strong><span>{asset.adapter_key ?? "internal"}</span><em className={asset.status.includes("stale") ? "stale" : "fresh"}>{asset.status}</em>{asset.stale_reason ? <small><TriangleAlert />{asset.stale_reason}</small> : null}</div>)}</div> : <p className="empty-world">尚无制作资产。</p>}
        </article>
      </section>
    </main>
  );
}
